import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { QUERY_KEYS, queryClient } from '@src/query';
import { getConversationMessages, sendMessage } from '@src/queries/messaging/api';
import { MESSAGE_REFRESH_INTERVAL_MS } from '@src/config';
import { ApiTypes } from 'common';
import { Dm } from '@adalong/schemas';
import { ConversationData } from './useConversationDataManager';

const DEFAULT_NETWORK = 'instagram';

export interface MessageManagerState {
  messages: Dm[] | undefined;
  isLoading: boolean;
  isSending: boolean;
  error: string | null;
  sendMessage: (text: string) => Promise<void>;
  nextRefreshIn: number | null; // Seconds until next auto-refresh
  refreshStatus: 'idle' | 'refreshing' | 'success' | 'error'; // Current refresh status
}

/**
 * Enhanced message manager for a single conversation
 * - Fetches messages with automatic refresh every xx seconds (defined in consts)
 * - Prioritizes remote messages over stored messages
 * - Handles message sending with immediate refresh
 * - Clear error handling and retry logic
 * - Live countdown for next refresh with proper status display
 */
export function useMessageManager(
  conversation: ConversationData | undefined,
  options: {
    autoRefreshInterval?: number;
    enabled?: boolean;
  } = {},
): MessageManagerState {
  const { autoRefreshInterval = MESSAGE_REFRESH_INTERVAL_MS, enabled = true } = options;

  // State management
  const [nextRefreshIn, setNextRefreshIn] = useState<number | null>(null);
  const [refreshStatus, setRefreshStatus] = useState<'idle' | 'refreshing' | 'success' | 'error'>(
    'idle',
  );

  // Refs for tracking
  const lastMessageCountRef = useRef<number>(0);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const isActivelyRefreshingRef = useRef<boolean>(false);
  const secondsLeftRef = useRef<number>(0);

  // Build query parameters - memoized to prevent unnecessary re-renders
  const queryParams = useMemo((): ApiTypes.ParamsGetConversationMessages | null => {
    if (!conversation) return null;

    return {
      interlocutorId: conversation.interlocutorId,
      targetAccountId: conversation.targetId,
      network: DEFAULT_NETWORK,
      conversationId: conversation.id,
    };
  }, [conversation]);

  const queryKey = queryParams ? QUERY_KEYS.conversationMessages(conversation!.id) : null;

  // Fetch messages with optimized settings
  const {
    data: messagesData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKey!,
    queryFn: () => getConversationMessages(queryParams!),
    enabled: enabled && !!queryParams,
    staleTime: 0, // Always consider stale to force fresh data
    gcTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false, // Disable auto refetch to control it manually
    refetchOnReconnect: true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  // Handle messages data updates (was onSuccess callback)
  useEffect(() => {
    if (messagesData) {
      const newMessageCount = messagesData?.Dms?.length || 0;
      lastMessageCountRef.current = newMessageCount;
      if (isActivelyRefreshingRef.current) {
        setRefreshStatus('success');
        setTimeout(() => setRefreshStatus('idle'), 3000);
        isActivelyRefreshingRef.current = false;
      }
    }
  }, [messagesData]);

  // Handle query error (was onError callback)
  useEffect(() => {
    if (error && isActivelyRefreshingRef.current) {
      setRefreshStatus('error');
      setTimeout(() => setRefreshStatus('idle'), 5000);
      isActivelyRefreshingRef.current = false;
    }
  }, [error]);

  // Message sending mutation
  const {
    mutateAsync: sendMessageMutation,
    isPending: isSending,
    error: sendError,
  } = useMutation<any, Error, ApiTypes.ParamsSendDm>({
    mutationFn: (params) => sendMessage(params),
    onSuccess: async () => {
      // Trigger immediate refresh after sending
      isActivelyRefreshingRef.current = true;
      setRefreshStatus('refreshing');

      // Force refresh with small delay
      setTimeout(async () => {
        try {
          if (queryKey) {
            await queryClient.invalidateQueries({ queryKey });
          }
          await refetch();
        } catch (error) {
          console.error('Failed to refresh after sending message:', error);
        }
      }, 500);
    },
    onError: (error) => {
      console.error('Failed to send message:', error);
    },
  });

  // Handle message sending
  const handleSendMessage = useCallback(
    async (text: string): Promise<void> => {
      if (!conversation || !text.trim()) {
        throw new Error('Invalid message or conversation');
      }

      await sendMessageMutation({
        body: text.trim(),
        interlocutorId: conversation.interlocutorId,
        senderAccountId: conversation.targetId,
        networkName: DEFAULT_NETWORK,
      });
    },
    [conversation, sendMessageMutation],
  );

  // Simplified auto-refresh system
  useEffect(() => {
    // Only start if we have a conversation and it's enabled
    if (!conversation?.id || !enabled) {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
      setNextRefreshIn(null);
      return;
    }

    // Clear any existing interval
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
    }

    // Initialize countdown using ref to avoid closure issues
    secondsLeftRef.current = Math.ceil(autoRefreshInterval / 1000);
    setNextRefreshIn(secondsLeftRef.current);

    // Set up interval for countdown and refresh
    intervalIdRef.current = setInterval(async () => {
      // Prevent multiple refreshes
      if (isActivelyRefreshingRef.current) {
        return;
      }

      // Decrement countdown using ref
      secondsLeftRef.current -= 1;

      if (secondsLeftRef.current > 0) {
        setNextRefreshIn(secondsLeftRef.current);
      } else {
        // Time to refresh
        setNextRefreshIn(null);
        isActivelyRefreshingRef.current = true;
        setRefreshStatus('refreshing');

        // Reset countdown immediately for next cycle
        secondsLeftRef.current = Math.ceil(autoRefreshInterval / 1000);
        setNextRefreshIn(secondsLeftRef.current);

        // Perform refresh
        try {
          if (queryKey) {
            await queryClient.invalidateQueries({ queryKey });
          }
          await refetch();
        } catch (error) {
          console.warn('Scheduled refresh failed:', error);
        } finally {
          isActivelyRefreshingRef.current = false;
        }
      }
    }, 1000);

    // Cleanup
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, [conversation?.id, enabled, autoRefreshInterval]);

  // Extract and sort messages - memoized for performance
  const messages = useMemo(() => {
    return messagesData?.Dms?.sort((a, b) => {
      const aTime = new Date(a.receivedDate || a.created_at || '').getTime();
      const bTime = new Date(b.receivedDate || b.created_at || '').getTime();
      return aTime - bTime; // Oldest first
    });
  }, [messagesData]);

  // Simplified error message
  const errorMessage = useMemo(() => {
    if (sendError) return `Failed to send message: ${sendError.message}`;
    if (error) return `Failed to load messages: ${(error as any).message}`;
    return null;
  }, [error, sendError]);

  return {
    messages,
    isLoading,
    isSending,
    error: errorMessage,
    sendMessage: handleSendMessage,
    nextRefreshIn,
    refreshStatus,
  };
}
